import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck,
  Scale,
  BookOpen,
  ChevronRight,
  ArrowUpRight
} from 'lucide-react';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';

const Terms = () => {
  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">LEGAL</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 tracking-tight">
            Terms of Service
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Please read these terms carefully before using NeoPass Learning platform.
          </p>
          <p className="text-white/40 text-sm mt-4">
            Last updated: January 2026
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* Acceptance of Terms */}
          <div className="border-b border-white/10 pb-12">
            <h2 className="text-2xl font-medium text-white mb-6">1. Acceptance of Terms</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              By accessing and using NeoPass Learning ("the Service"), you accept and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our service.
            </p>
            <p className="text-white/70 leading-relaxed">
              We reserve the right to update these terms at any time. Continued use of the Service after changes constitutes acceptance of the modified terms.
            </p>
          </div>

          {/* Service Description */}
          <div className="border-b border-white/10 pb-12">
            <h2 className="text-2xl font-medium text-white mb-6">2. Service Description</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              NeoPass Learning is an AI-powered educational platform designed to assist students with:
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4 mb-4">
              <li>Self-study and concept understanding</li>
              <li>Homework assistance and problem solving</li>
              <li>Exam preparation and practice</li>
              <li>Step-by-step explanations of complex topics</li>
            </ul>
            <p className="text-white/70 leading-relaxed">
              The Service is provided through a browser extension compatible with Chrome, Edge, and Brave browsers.
            </p>
          </div>

          {/* Fair Use Policy */}
          <div className="border-b border-white/10 pb-12">
            <div className="flex items-center gap-3 mb-6">
              <Scale className="h-6 w-6 text-accent" />
              <h2 className="text-2xl font-medium text-white">3. Fair Use Policy</h2>
            </div>
            <div className="border border-accent\/30 bg-accent\/5 p-6 rounded-lg mb-6">
              <p className="text-accent\/90 font-medium mb-2">
                ⚠️ Important Notice
              </p>
              <p className="text-white/70">
                NeoPass Learning is designed exclusively for self-study, homework assistance, and exam preparation. 
                <strong className="text-white"> This tool must not be used during live proctored examinations.</strong>
              </p>
            </div>
            <p className="text-white/70 leading-relaxed">
              By using our service, you agree to use it responsibly for educational purposes only. Misuse of the platform, including but not limited to academic dishonesty, may result in immediate account suspension without refund.
            </p>
          </div>

          {/* User Accounts */}
          <div className="border-b border-white/10 pb-12">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="h-6 w-6 text-accent" />
              <h2 className="text-2xl font-medium text-white">4. User Accounts</h2>
            </div>
            <p className="text-white/70 leading-relaxed mb-4">
              When you create an account with us, you must provide accurate and complete information. You are responsible for:
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4 mb-4">
              <li>Maintaining the security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
            </ul>
            <div className="bg-white/5 border border-white/10 p-4 rounded">
              <p className="text-white/60 text-sm">
                <strong className="text-white/80">Note:</strong> For security reasons, password reset functionality is not available. Please store your credentials safely.
              </p>
            </div>
          </div>

          {/* Credits and Payments */}
          <div className="border-b border-white/10 pb-12">
            <h2 className="text-2xl font-medium text-white mb-6">5. Credits and Payments</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              NeoPass Learning operates on a credit-based system. Credits are purchased through our secure payment partner, Razorpay.
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4 mb-4">
              <li>Credits do not expire and remain in your account until used</li>
              <li>Credits are non-transferable between accounts</li>
              <li>All prices are listed in Indian Rupees (INR)</li>
            </ul>
            <p className="text-white/70 leading-relaxed">
              For refund information, please see our <Link to="/refund-policy" className="text-accent hover:text-accent-light">Refund Policy</Link>.
            </p>
          </div>

          {/* Intellectual Property */}
          <div className="border-b border-white/10 pb-12">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="h-6 w-6 text-accent" />
              <h2 className="text-2xl font-medium text-white">6. Intellectual Property</h2>
            </div>
            <p className="text-white/70 leading-relaxed mb-4">
              The Service, including all content, features, and functionality, is owned by NeoPass Learning and is protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-white/70 leading-relaxed">
              AI-generated responses are provided for educational purposes only. You may use these responses for personal learning but may not redistribute, sell, or claim them as your own original work.
            </p>
          </div>

          {/* Limitation of Liability */}
          <div className="border-b border-white/10 pb-12">
            <h2 className="text-2xl font-medium text-white mb-6">7. Limitation of Liability</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              The Service is provided "as is" without warranties of any kind. NeoPass Learning shall not be liable for:
            </p>
            <ul className="list-disc list-inside text-white/70 space-y-2 ml-4">
              <li>Any indirect, incidental, or consequential damages</li>
              <li>Loss of data, profits, or academic outcomes</li>
              <li>Service interruptions or technical issues</li>
              <li>Accuracy of AI-generated content</li>
            </ul>
          </div>

          {/* Termination */}
          <div className="border-b border-white/10 pb-12">
            <h2 className="text-2xl font-medium text-white mb-6">8. Termination</h2>
            <p className="text-white/70 leading-relaxed">
              We may terminate or suspend your account immediately, without prior notice, for any breach of these Terms of Service. Upon termination, your right to use the Service will cease immediately. Any unused credits may be forfeited depending on the nature of the violation.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-2xl font-medium text-white mb-6">9. Contact Information</h2>
            <p className="text-white/70 leading-relaxed mb-4">
              For questions about these Terms of Service, please contact us at:
            </p>
            <a 
              href="mailto:neopassbusiness@gmail.com" 
              className="text-accent hover:text-accent-light transition-colors"
            >
              neopassbusiness@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-6">
            Questions about our terms?
          </h2>
          <p className="text-white/50 mb-10 max-w-xl mx-auto">
            Feel free to reach out to us if you have any questions about our terms and policies.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors"
            >
              Contact Us
              <ChevronRight className="h-4 w-4" />
            </Link>
            <Link 
              to="/refund-policy"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
            >
              View Refund Policy
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer variant="full" activeLink="terms" />
    </div>
  );
};

export default Terms;
